import apiClient from '../client'
import type { KnowledgeEntry, KnowledgeFilters, KnowledgeStats, PaginatedResponse } from '@/types'

// Transform backend response to frontend PaginatedResponse format
const transformKnowledgeResponse = (
  entries: KnowledgeEntry[],
  page: number,
  pageSize: number
): PaginatedResponse<KnowledgeEntry> => {
  const total = entries.length
  return {
    items: entries,
    total: total,
    page: page,
    page_size: pageSize,
    total_pages: Math.ceil(total / pageSize) || 1,
  }
}

export interface CreateKnowledgeData {
  title: string
  content: string
  category: string
  content_type: string
  tags?: string[]
  is_active?: boolean
  is_verified?: boolean
}

export interface UpdateKnowledgeData {
  title?: string
  content?: string
  category?: string
  content_type?: string
  tags?: string[]
  is_active?: boolean
  is_verified?: boolean
}

export const knowledgeApi = {
  getAll: async (filters: KnowledgeFilters = {}) => {
    const page = filters.page || 1
    const pageSize = filters.page_size || 25

    const response = await apiClient.get<KnowledgeEntry[]>('/api/knowledge/entries', { params: filters })

    return {
      ...response,
      data: transformKnowledgeResponse(response.data || [], page, pageSize),
    }
  },

  getById: (id: string) =>
    apiClient.get<KnowledgeEntry>(`/api/knowledge/entries/${id}`),

  getStats: () =>
    apiClient.get<KnowledgeStats>('/api/knowledge/stats'),

  getCategories: () =>
    apiClient.get<{ categories: Array<{ category: string; count: number }> }>('/api/knowledge/categories'),

  getDocTypes: () =>
    apiClient.get<{ doc_types: Array<{ doc_type: string; count: number }> }>('/api/knowledge/doc-types'),

  create: (data: CreateKnowledgeData) =>
    apiClient.post<KnowledgeEntry>('/api/knowledge/entries', {
      content: data.content,
      doc_type: data.content_type,
      source: 'manual',
      metadata: {
        title: data.title,
        category: data.category,
        tags: data.tags || [],
        verified: data.is_verified || false,
      }
    }),

  update: (id: string, data: UpdateKnowledgeData) =>
    apiClient.put<KnowledgeEntry>(`/api/knowledge/entries/${id}`, {
      content: data.content,
      doc_type: data.content_type,
      is_active: data.is_active,
      metadata: {
        title: data.title,
        category: data.category,
        tags: data.tags,
        verified: data.is_verified,
      }
    }),

  delete: (id: string) =>
    apiClient.delete(`/api/knowledge/entries/${id}`),

  verify: (id: string) =>
    apiClient.put(`/api/knowledge/entries/${id}`, {
      metadata: { verified: true }
    }),

  search: (query: string, top_k?: number, doc_types?: string[]) =>
    apiClient.post<Array<{ entry_id: number; content: string; doc_type: string; similarity_score: number }>>('/api/knowledge/search', {
      query,
      top_k: top_k || 5,
      doc_types
    }),
}
